/**
 * THE KPI STRIP — sticky, six tiles, each a control.
 *
 * ── ITERATED FROM THE RESPONSE, NOT FROM A FIXED LIST ──
 * `HEADLINE_KPI_KEYS` is a display ORDER, filtered against what the overview
 * actually returned. A figure the server did not send never gets a tile; a
 * figure it did send that is not in the order appears in the "every figure"
 * table on Overview rather than silently nowhere.
 *
 * ── EVERY TILE IS A BUTTON ──
 * Pressing one applies the filter it describes and lights up. The desk feed's
 * stat strip already worked this way and the reason is the same: four numbers
 * describing a narrowing the reader then has to perform by hand, on a screen
 * whose whole argument is that the answer is already there.
 *
 * ── THE FOUR DELTA RULES ARE NOT NEGOTIABLE ──
 * A `live` figure gets no chip at all; an unlike pair gets no percentage; a
 * percentage against zero is an absolute. All four live in <Delta>.
 *
 * ── COMPARISON MODE SPLITS THE TILE, IT DOES NOT ADD A ROW ──
 * With a second scope chosen, each tile carries both figures with the two scope
 * names above them and the difference between. Two STRIPS would put the same
 * label twice on one screen with different numbers under it, which is the
 * arrangement most likely to be misread — and the difference, the thing the
 * reader opened comparison mode for, would not be anywhere at all.
 *
 * The period-over-period delta is suppressed while comparing. Two deltas on one
 * tile — against last month and against the other building — is two arrows
 * pointing at different questions.
 */
import React, { useRef } from 'react';
/* WP-34 — the shared scroll affordance. The overflow here is deliberate; what
   was missing is any signal that it exists. */
import { ScrollStrip, SnapDots } from '../../../../scroll/scroll-affordance';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  AnalyticsBatchResult,
  AnalyticsOverview,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { Delta } from '../charts/chart-delta';
import { DashSimpleTooltip } from '../charts/chart-tooltip';
import { Skeleton, SkeletonStack } from '../charts/chart.styled';
import { GRADIENT, cursor, fillUrl, format } from '../charts/chart-theme';
import { StateSwitch } from '../../../../../motion';
import {
  HEADLINE_KPI_KEYS,
  KPI_LABELS,
  KPI_SPARK_MEASURE,
  PANEL,
  SPARK_PREFIX,
} from '../data/panel-plan';
import {
  KpiFigure,
  KpiFoot,
  KpiLabel,
  KpiSpark,
  KpiStrip as Strip,
  KpiTile,
  LiveDot,
  LiveTag,
  SplitDiff,
  SplitName,
  SplitRow,
  SplitSide,
} from '../dashboard-screen.styled';

export const kpiLabel = (key: string): string => KPI_LABELS[key] || format.humanise(key);

/** The headline keys the response actually contains, in display order. Nothing
 *  is invented: a figure the server did not send does not get a tile. */
export const headlineKeys = (overview: AnalyticsOverview): string[] =>
  HEADLINE_KPI_KEYS.filter(key => overview.kpis[key] != null);

interface SparkPoint {
  key: string;
  label: string;
  value: number;
}

const Sparkline: React.FC<{ result?: AnalyticsBatchResult; unit: string }> = ({
  result,
  unit,
}) => {
  if (!result || isBatchFailure(result)) return null;
  const points: SparkPoint[] = result.items
    .map(item => ({
      key: item.key,
      label: format.dayKey(item.key),
      value: format.numberOf(item.value) ?? 0,
    }))
    .filter(p => p.key);
  // Two points is the minimum that can describe a direction. One is a dot
  // pretending to be a trend.
  if (points.length < 2) return null;

  return (
    /* DECORATIVE, AND SAYING SO. This sparkline redraws a number the tile
       already prints in text beside it. It gets no accessibilityLayer for the
       same reason: six focusable sparklines would be six tab stops that each
       announce what the adjacent text just said, in front of every real control
       on the screen. */
    <KpiSpark aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Tooltip
            content={
              <DashSimpleTooltip
                tone={dataViz.series[0]}
                valueKey="value"
                labelKey="label"
                noun={[unit, unit]}
              />
            }
            cursor={cursor.line}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={dataViz.series[0]}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={fillUrl(GRADIENT.area)}
            fillOpacity={1}
            dot={false}
            activeDot={{
              r: 4,
              fill: dataViz.series[0],
              stroke: palette.surface,
              strokeWidth: 2,
            }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </KpiSpark>
  );
};

/**
 * ONE TILE, TWO SCOPES.
 *
 * The difference is printed as an absolute AND as a share of the primary, and
 * the share is suppressed when the primary is zero — a percentage against a base
 * of nothing is a number with no meaning, which is the rule <Delta> obeys too.
 *
 * A `live` figure DOES get a difference: both sides are counted as of the same
 * instant, so unlike the period-over-period case there is something real to
 * subtract. What it does not get is a percentage.
 */
const SplitFigures: React.FC<{
  label: string;
  kpi: { value: number; kind: string };
  other?: { value: number };
  loading: boolean;
  labels: { primary: string; compare: string };
}> = ({ label, kpi, other, loading, labels }) => {
  const diff = other ? kpi.value - other.value : null;
  const share =
    other && kpi.value !== 0 && kpi.kind !== 'live'
      ? format.share(Math.abs(diff as number), kpi.value)
      : null;

  return (
    <SplitRow data-testid={`kpi-split-${label}`}>
      <SplitSide>
        <SplitName title={labels.primary}>{labels.primary}</SplitName>
        <KpiFigure data-testid="kpi-figure">{kpi.value.toLocaleString()}</KpiFigure>
      </SplitSide>
      <SplitSide $muted>
        <SplitName title={labels.compare}>{labels.compare}</SplitName>
        <KpiFigure data-testid="kpi-figure">
          {loading && !other ? '...' : other ? other.value.toLocaleString() : '—'}
        </KpiFigure>
      </SplitSide>
      {diff != null ? (
        <SplitDiff data-testid="kpi-split-diff">
          {diff > 0 ? '+' : diff < 0 ? '-' : ''}
          {Math.abs(diff).toLocaleString()}
          {share ? ` (${share})` : ''}
        </SplitDiff>
      ) : null}
    </SplitRow>
  );
};

export interface KpiStripProps {
  overview?: AnalyticsOverview;
  loading: boolean;
  /** Panel id -> result, for pulling each tile's sparkline out of the batch. */
  results: Record<string, AnalyticsBatchResult | undefined>;
  /** Which tile, if any, is currently driving a filter. */
  activeKey?: string | null;
  onSelect: (key: string) => void;
  /** The second scope's figures. Absent unless comparison mode is on. */
  compare?: AnalyticsOverview;
  /** Still in flight. The tile shows the primary figure and a placeholder for
   *  the second rather than blanking — the first scope's answer is not less true
   *  because the second has not arrived. */
  compareLoading?: boolean;
  /** What to call each side. Presence of this is what turns the split on. */
  scopeLabels?: { primary: string; compare: string };
}

export const KpiStripView: React.FC<KpiStripProps> = ({
  overview,
  loading,
  results,
  activeKey,
  onSelect,
  compare,
  compareLoading,
  scopeLabels,
}) => {
  /* The scroller the affordances read. It has to be the element that actually
     scrolls — the fades and the dots are both derived from its scrollLeft. */
  const stripRef = useRef<HTMLDivElement>(null);

  /* Skeleton tiles → figures is a crossfade of the same strip, not a swap:
     the skeleton is shaped like the tiles so nothing around it moves. */
  if (loading || !overview) {
    return (
      <StateSwitch state="loading">
        <Strip aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <KpiTile key={i} type="button" disabled>
              <SkeletonStack aria-hidden="true">
                <Skeleton h={10} w="60%" />
                <Skeleton h={30} w="45%" />
                <Skeleton h={36} />
              </SkeletonStack>
            </KpiTile>
          ))}
        </Strip>
      </StateSwitch>
    );
  }

  const keys = headlineKeys(overview);
  const comparing = Boolean(scopeLabels);

  const sparkFor = (key: string): AnalyticsBatchResult | undefined => {
    const measure = KPI_SPARK_MEASURE[key];
    if (!measure) return undefined;
    if (measure === 'entries') return results[PANEL.pulseEntries];
    if (measure === 'incidents') return results[PANEL.pulseIncidents];

    return results[`${SPARK_PREFIX}${key}`];
  };

  return (
    <StateSwitch state="ready">
    <ScrollStrip scroller={stripRef}>
    <Strip data-testid="kpi-strip" ref={stripRef}>
      {keys.map(key => {
        const kpi = overview.kpis[key];
        const label = kpiLabel(key);
        const active = activeKey === key;

        return (
          <KpiTile
            key={key}
            type="button"
            $active={active}
            aria-pressed={active}
            data-testid={`kpi-${key}`}
            onClick={() => onSelect(key)}
            title={
              // The tooltip on a LIVE figure has to say AS OF NOW rather than as
              // of the window end. read-model.js is explicit that
              // arrivals_still_open "is NOT an as-of-:to figure and must not be
              // phrased as one", and the tile is where that phrasing happens.
              kpi.kind === 'live'
                ? `${label}: counted as of now, not as of the end of the period`
                : kpi.kind === 'as_of'
                ? `${label}: the standing total as of the end of the period`
                : `${label}: counted inside the period`
            }
          >
            <KpiLabel>{label}</KpiLabel>

            {comparing ? (
              <SplitFigures
                label={label}
                kpi={kpi}
                other={compare?.kpis?.[key]}
                loading={Boolean(compareLoading)}
                labels={scopeLabels as { primary: string; compare: string }}
              />
            ) : (
              <KpiFigure data-testid="kpi-figure">{kpi.value.toLocaleString()}</KpiFigure>
            )}

            <KpiFoot>
              {kpi.kind === 'live' ? (
                <LiveTag>
                  <LiveDot aria-hidden="true" />
                  as of now
                </LiveTag>
              ) : null}
              {/* ONE ARROW PER TILE. See the header: while comparing, the
                  period-over-period chip would be a second arrow answering a
                  different question. */}
              {comparing ? null : (
                <Delta
                  delta={kpi.delta}
                  compareValue={kpi.compareValue}
                  likeForLike={overview.likeForLike}
                  curSpanMs={overview.curSpanMs}
                  prevSpanMs={overview.prevSpanMs}
                  kind={kpi.kind}
                />
              )}
            </KpiFoot>
            {comparing ? null : (
              <Sparkline result={sparkFor(key)} unit={label.toLowerCase()} />
            )}
          </KpiTile>
        );
      })}
    </Strip>
    </ScrollStrip>
    {/* WHERE YOU ARE, not just that there is more. The strip is a snap carousel
        below md and its 32% peek is the design; what it never said is how many
        tiles there are or which one you are on. Derived from the scroll offset,
        because a carousel is scrolled by finger and there is no event that says
        "the reader chose tile 3". Hidden from assistive tech: the tiles are all
        reachable as buttons, and a dot announcing "3 of 6" would be a second,
        worse way to say what the list already says. */}
    <SnapDots scroller={stripRef} count={keys.length} />
    </StateSwitch>
  );
};
